import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, Sparkles } from "lucide-react";
import { MaskedLines } from "@/components/Reveal";

export default function Hero({ start }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 100]);
  const ringY = useTransform(scrollYProgress, [0, 1], [0, -80]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative overflow-hidden pt-36 sm:pt-44 pb-24">
      <div className="absolute -top-40 -left-40 w-[560px] h-[560px] rounded-full bg-blush/15 blur-[120px]" aria-hidden="true" />
      <div className="absolute top-1/3 -right-40 w-[480px] h-[480px] rounded-full bg-gold/15 blur-[120px]" aria-hidden="true" />

      <div className="max-w-[1400px] mx-auto px-6 sm:px-10 grid lg:grid-cols-12 gap-16 items-center relative">
        <motion.div className="lg:col-span-7 relative z-10" style={{ opacity: fade }}>
          <motion.p
            className="flex items-center gap-3 text-[12px] tracking-[0.45em] uppercase text-coral mb-10"
            initial={{ opacity: 0, y: 16 }}
            animate={start ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.15 }}
          >
            <Sparkles size={14} strokeWidth={1.5} />
            Bespoke Dancefloors · Greater Boston / New England
          </motion.p>

          <h1 className="font-serif font-light text-ink leading-[0.95] tracking-tight text-5xl sm:text-6xl lg:text-7xl">
            <MaskedLines
              start={start}
              delay={0.25}
              lines={[
                <>Where your biggest</>,
                <><span className="italic text-coral">centerpiece</span> becomes</>,
                <>an unforgettable</>,
                <><span className="italic">dream come true</span><span className="text-gold">.</span></>,
              ]}
            />
          </h1>

          <motion.p
            className="mt-10 max-w-md text-base sm:text-lg font-light text-ink/70 leading-relaxed"
            initial={{ opacity: 0, y: 24 }}
            animate={start ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.9, delay: 0.85 }}
          >
            Seamless all-white, liquid mirror dancefloors, and LED dancefloors —
            measured, hand-laid and polished so your first dance begins on pure magic.
          </motion.p>

          <motion.div
            className="mt-12 flex flex-wrap items-center gap-5"
            initial={{ opacity: 0, y: 24 }}
            animate={start ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.9, delay: 1.05 }}
          >
            <a
              href="tel:9784065564"
              data-testid="hero-phone-link"
              className="flex items-center gap-3 text-sm tracking-wider text-ink/80 hover:text-coral transition-colors duration-300 group"
            >
              <span className="w-11 h-11 rounded-full border border-ink/20 flex items-center justify-center group-hover:border-coral group-hover:text-coral transition-colors duration-300">
                <Phone size={15} strokeWidth={1.5} />
              </span>
              978-406-5564
            </a>
          </motion.div>
        </motion.div>

        <div className="lg:col-span-5 relative">
          <motion.div
            className="absolute -inset-8 border border-gold/40 rounded-t-[999px] rounded-b-[2rem]"
            style={{ y: ringY }}
            initial={{ opacity: 0 }}
            animate={start ? { opacity: 1 } : {}}
            transition={{ duration: 1.2, delay: 1.2 }}
            aria-hidden="true"
          />
          <motion.div
            className="relative rounded-t-[999px] rounded-b-[2rem] overflow-hidden spotlight-frame aspect-[3/4]"
            initial={{ clipPath: "inset(100% 0 0 0)" }}
            animate={start ? { clipPath: "inset(0% 0 0 0)" } : {}}
            transition={{ duration: 1.3, delay: 0.6, ease: [0.76, 0, 0.24, 1] }}
          >
            <motion.img
              src="/assets/floors/bw-patterned.webp"
              alt="Black and white patterned dancefloor overlooking the Boston harbor skyline through floor-to-ceiling windows"
              className="w-full h-[135%] -mt-[15%] object-cover"
              style={{ y: imgY }}
            />
          </motion.div>
          <motion.div
            className="absolute -bottom-8 -left-8 sm:-left-14 bg-ivory/85 backdrop-blur-xl rounded-2xl p-4 flex items-center gap-4 shadow-[0_24px_60px_-20px_rgba(45,40,51,0.35)]"
            initial={{ opacity: 0, y: 30 }}
            animate={start ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.9, delay: 1.4 }}
          >
            <img src="/assets/floors/starlit.webp" alt="Starlit LED floor" className="w-16 h-16 rounded-xl object-cover" />
            <div>
              <p className="text-[11px] tracking-[0.3em] uppercase text-gold">Experience</p>
              <p className="font-serif italic text-lg text-ink">The Starlit Floor</p>
            </div>
          </motion.div>
        </div>
      </div>

      <motion.div
        data-testid="scroll-indicator"
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3"
        style={{ opacity: fade }}
        initial={{ opacity: 0 }}
        animate={start ? { opacity: 1 } : {}}
        transition={{ delay: 1.8, duration: 1 }}
      >
        <span className="text-[10px] tracking-[0.5em] uppercase text-ink/40">Scroll</span>
        <span className="relative w-px h-10 bg-ink/15 overflow-hidden">
          <span className="absolute top-0 left-0 w-full h-3 bg-coral animate-scroll-dot" />
        </span>
      </motion.div>
    </section>
  );
}
